import {makeAutoObservable} from "mobx";
import {Project} from "../../entity/model/project";
import {RequestBody} from "../../entity/core/request-body";
import {ProjectStats} from "../../entity/resource/project-stats";
import ProjectAgent from "../agent/project-agent";
import {ProjectPermission} from "../../entity/resource/project-permission";

export default class ProjectStore {
  projectAgent: ProjectAgent;
  cache: boolean = false;
  permissions: ProjectPermission[] = [];

  constructor(projectAgent: ProjectAgent) {
    makeAutoObservable(this);
    this.projectAgent = projectAgent;
  }

  getProjects = async (organizationId: string, isAdminUser: boolean): Promise<Project[]> => {
    try {
      const response = await this.projectAgent.getProjects(organizationId, isAdminUser);
      return Array.isArray(response) ? response : response.projects;
    } catch (error: any) {
      console.error('Error occurred while fetching the projects', error);
    }

    return [];
  }

  getProjectStats = async (organizationId: string): Promise<ProjectStats | undefined> => {
    try {
      return await this.projectAgent.getProjectStats(organizationId);
    } catch (error: any) {
      console.error('Error occurred while fetching the projects stats', error);
    }
  }

  createProject = async (organizationId: string, requestBody: RequestBody): Promise<string | undefined> => {
    try {
      const response = await this.projectAgent.createProject(organizationId, requestBody);

      if (response)
        this.cache = false;
    } catch (error: any) {
      console.error('Error occurred while creating the project', error);
      return 'Error occurred while creating the project !!';
    }
  }

  updateProject = async (organizationId: string, projectId: string, requestBody: RequestBody): Promise<string | undefined> => {
    try {
      const response = await this.projectAgent.updateProject(organizationId, projectId, requestBody);

      if (response)
        this.cache = false;
    } catch (error: any) {
      console.error('Error occurred while updating the project', error);
      return 'Error occurred while updating the project !!';
    }
  }

  assignProject = async (organizationId: string, projectId: string, requestBody: RequestBody): Promise<string | undefined> => {
    try {
      await this.projectAgent.assignProject(organizationId, projectId, requestBody);
      this.cache = false;
    } catch (error: any) {
      console.error('Error occurred while assigning the project', error);
      return 'Error occurred while assigning the project !!';
    }
  }

  moveProjectToOnHold = async (organizationId: string, projectId: string): Promise<string | undefined> => {
    try {
      await this.projectAgent.moveProjectToOnHold(organizationId, projectId);
    } catch (error: any) {
      console.error('Error occurred while moving the project to on hold', error);
      return 'Error occurred while moving the file to on hold !!';
    }
  }

  moveProjectFromOnHold = async (organizationId: string, projectId: string): Promise<string | undefined> => {
    try {
      await this.projectAgent.moveProjectFromOnHold(organizationId, projectId);
    } catch (error: any) {
      console.error('Error occurred while moving the project from on hold', error);
      return 'Error occurred while moving the file from on hold !!';
    }
  }

  deleteProject = async (organizationId: string, projectId: string): Promise<string | undefined> => {
    try {
      await this.projectAgent.deleteProject(organizationId, projectId);
    } catch (error: any) {
      console.error('Error occurred while deleting the project', error);
      return 'Error occurred while deleting the project !!';
    }
  }

  getProjectById = async (organizationId: string, projectId: string): Promise<Project | undefined> => {
    try {
      const response = await this.projectAgent.getProjectById(organizationId, projectId);
      if (response)
        this.cache = false;

      return response;
    } catch(error: any) {
      console.error('Error occurred while fetching the project by id', error);
    }
  }
}
